// src/pages/Asesores.jsx
import { useState, useEffect } from 'react';
import { workforceApi } from '../lib/workforce';

// Hook para detectar el tema
const useTheme = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const bgColor = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (bgColor) {
        return bgColor.startsWith('#0') || bgColor.startsWith('#1');
      }
    }
    
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const root = document.documentElement;
      const bgColor = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (bgColor) {
        const isDark = bgColor.startsWith('#0') || bgColor.startsWith('#1');
        setIsDarkMode(isDark);
      }
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });
    
    const handleStorageChange = () => {
      const stored = localStorage.getItem('theme');
      if (stored) setIsDarkMode(stored === 'dark');
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  return { isDarkMode };
};

export default function Asesores() {
  const { isDarkMode } = useTheme();
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAsesor, setFiltroAsesor] = useState('');
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

  useEffect(() => {
    cargarHorarios();
    
    const interval = setInterval(() => {
      cargarHorarios(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const cargarHorarios = async (silencioso = false) => {
    try {
      if (!silencioso) {
        setLoading(true);
      }
      setError(null);
      const response = await workforceApi.getHorarios();
      const data = response?.data ?? response;
      setHorarios(Array.isArray(data) ? data : []);
      setUltimaActualizacion(new Date());
    } catch (err) {
      console.error('Error al cargar horarios:', err);
      if (!silencioso) {
        setError('No se pudieron cargar los horarios');
      }
    } finally {
      if (!silencioso) {
        setLoading(false);
      }
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatearHora = (hora) => {
    if (!hora) return 'N/A';
    const [h, m] = hora.split(':');
    return `${h}:${m}`;
  };

  const formatearTiempoTranscurrido = (fecha) => {
    if (!fecha) return '';
    
    const ahora = new Date();
    const diff = Math.floor((ahora - fecha) / 1000);
    
    if (diff < 60) return 'hace unos segundos';
    if (diff < 3600) {
      const mins = Math.floor(diff / 60);
      return `hace ${mins} ${mins === 1 ? 'minuto' : 'minutos'}`;
    }
    
    const horas = Math.floor(diff / 3600);
    return `hace ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  };

  const horariosFiltrados = horarios.filter(h => 
    !filtroAsesor || 
    h.asesor_nombre?.toLowerCase().includes(filtroAsesor.toLowerCase())
  );

  const styles = {
    container: {
      padding: 24,
      maxWidth: 1400,
      margin: '0 auto',
      minHeight: '100vh',
      background: isDarkMode ? '#0a0a0a' : '#fafafa'
    },
    header: {
      marginBottom: 32,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 16
    },
    headerLeft: {
      flex: 1
    },
    actualizacionInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 16px',
      background: isDarkMode ? '#1a1a1a' : '#ffffff',
      borderRadius: 20,
      border: `1px solid ${isDarkMode ? '#2a2a2a' : '#e5e5e5'}`,
      fontSize: 13,
      color: isDarkMode ? '#a0a0a0' : '#525252',
      fontFamily: "'Poppins', sans-serif",
    },
    puntoParpadeante: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: '#82cc0e',
      animation: 'pulse 2s ease-in-out infinite'
    },
    title: {
      fontSize: 32,
      fontWeight: 700,
      color: isDarkMode ? '#ffffff' : '#0a0a0a',
      marginBottom: 8,
      fontFamily: "'Poppins', sans-serif",
    },
    subtitle: {
      fontSize: 16,
      color: isDarkMode ? '#a0a0a0' : '#525252',
      fontFamily: "'Poppins', sans-serif",
    },
    searchBar: {
      marginBottom: 24,
      display: 'flex',
      gap: 16,
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    searchInput: {
      flex: 1,
      minWidth: 250,
      padding: '12px 16px',
      border: `2px solid ${isDarkMode ? '#2a2a2a' : '#e5e5e5'}`,
      borderRadius: 12,
      fontSize: 15,
      fontFamily: "'Poppins', sans-serif",
      transition: 'all 0.2s',
      outline: 'none',
      background: isDarkMode ? '#1a1a1a' : '#ffffff',
      color: isDarkMode ? '#ffffff' : '#0a0a0a'
    },
    refreshBtn: {
      padding: '12px 24px',
      background: '#82cc0e',
      color: '#ffffff',
      border: 'none',
      borderRadius: 12,
      fontSize: 15,
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
      gap: 20
    },
    card: {
      background: isDarkMode ? '#1a1a1a' : '#ffffff',
      borderRadius: 16,
      padding: 24,
      boxShadow: isDarkMode 
        ? '0 4px 12px rgba(0,0,0,0.5)' 
        : '0 2px 8px rgba(0,0,0,0.08)',
      border: `2px solid #82cc0e`,
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    cardHeader: {
      borderBottom: `2px solid ${isDarkMode ? '#2a2a2a' : '#f5f5f5'}`,
      paddingBottom: 16,
      marginBottom: 16
    },
    asesorNombre: {
      fontSize: 18,
      fontWeight: 600,
      color: isDarkMode ? '#ffffff' : '#0a0a0a',
      marginBottom: 8,
      fontFamily: "'Poppins', sans-serif",
    },
    cargo: {
      display: 'inline-block',
      padding: '6px 14px',
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "'Poppins', sans-serif",
      background: '#82cc0e',
      color: '#ffffff'
    },
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
      fontSize: 14,
      fontFamily: "'Poppins', sans-serif",
    },
    label: {
      color: isDarkMode ? '#737373' : '#737373',
      fontWeight: 500,
      minWidth: 80
    },
    value: {
      color: isDarkMode ? '#ffffff' : '#0a0a0a',
      fontWeight: 600
    },
    diasGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8
    },
    diaChip: {
      padding: '6px 12px',
      background: isDarkMode ? '#2a2a2a' : '#f5f5f5',
      color: isDarkMode ? '#ffffff' : '#0a0a0a',
      border: `1px solid ${isDarkMode ? '#3a3a3a' : '#e5e5e5'}`,
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 500,
      fontFamily: "'Poppins', sans-serif",
    },
    fechasContainer: {
      background: isDarkMode ? '#0f0f0f' : '#f9f9f9',
      padding: 12,
      borderRadius: 10,
      marginTop: 12,
      border: `1px solid ${isDarkMode ? '#2a2a2a' : '#e5e5e5'}`
    },
    loading: {
      textAlign: 'center',
      padding: 60,
      fontSize: 18,
      color: isDarkMode ? '#737373' : '#737373',
      fontFamily: "'Poppins', sans-serif",
    },
    error: {
      textAlign: 'center',
      padding: 60,
      background: isDarkMode ? '#1a1a1a' : '#ffffff',
      borderRadius: 16,
      border: `2px solid ${isDarkMode ? '#404040' : '#e5e5e5'}`,
      color: isDarkMode ? '#a0a0a0' : '#525252',
      fontSize: 16,
      fontFamily: "'Poppins', sans-serif",
    },
    emptyState: {
      textAlign: 'center',
      padding: 80,
      background: isDarkMode ? '#1a1a1a' : '#ffffff',
      borderRadius: 16,
      boxShadow: isDarkMode 
        ? '0 4px 12px rgba(0,0,0,0.5)' 
        : '0 2px 8px rgba(0,0,0,0.08)',
      border: `2px solid #82cc0e`
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: 16
    },
    emptyText: {
      fontSize: 18,
      color: isDarkMode ? '#737373' : '#737373',
      fontFamily: "'Poppins', sans-serif",
    },
    motivoBox: {
      marginTop: 12, 
      padding: 12, 
      background: isDarkMode ? '#2a2a2a' : '#f5f5f5',
      border: `1px solid ${isDarkMode ? '#3a3a3a' : '#e5e5e5'}`,
      borderRadius: 8,
      fontSize: 13,
      color: isDarkMode ? '#a0a0a0' : '#525252',
      fontFamily: "'Poppins', sans-serif",
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          Cargando horarios...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <div style={{ fontSize: 48, marginBottom: 16 }}></div>
          {error}
          <div style={{ marginTop: 16 }}>
            <button 
              style={styles.refreshBtn}
              onClick={() => cargarHorarios()}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Horarios de Asesores</h1>
          <p style={styles.subtitle}>
            Gestión y consulta de horarios del equipo
          </p>
        </div>
        
        {ultimaActualizacion && (
          <div style={styles.actualizacionInfo}>
            <div style={styles.puntoParpadeante} />
            <span>Actualizado {formatearTiempoTranscurrido(ultimaActualizacion)}</span>
          </div>
        )}
      </div>

      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Buscar por nombre de asesor..."
          value={filtroAsesor}
          onChange={(e) => setFiltroAsesor(e.target.value)}
          style={styles.searchInput}
          onFocus={(e) => e.target.style.borderColor = '#82cc0e'}
          onBlur={(e) => e.target.style.borderColor = isDarkMode ? '#2a2a2a' : '#e5e5e5'}
        />
      </div>

      {horariosFiltrados.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <div style={styles.emptyText}>
            {filtroAsesor 
              ? 'No se encontraron horarios con ese nombre'
              : 'No hay horarios registrados'}
          </div>
        </div>
      ) : (
        <div style={styles.grid}>
          {horariosFiltrados.map((horario) => (
            <div
              key={horario.id}
              style={styles.card}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = '#6bb00d';
                e.currentTarget.style.boxShadow = isDarkMode 
                  ? '0 12px 32px rgba(0,0,0,0.7), 0 0 20px rgba(130, 204, 14, 0.4)' 
                  : '0 8px 24px rgba(0,0,0,0.12), 0 0 20px rgba(130, 204, 14, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#82cc0e';
                e.currentTarget.style.boxShadow = isDarkMode 
                  ? '0 4px 12px rgba(0,0,0,0.5)' 
                  : '0 2px 8px rgba(0,0,0,0.08)';
              }}
            >
              <div style={styles.cardHeader}>
                <div style={styles.asesorNombre}>
                  {horario.asesor_nombre}
                </div>
                <span style={styles.cargo}>
                  {horario.asesor_cargo}
                </span>
              </div>

              <div style={styles.infoRow}>
                <span style={styles.label}>Entrada:</span>
                <span style={styles.value}>
                  {formatearHora(horario.hora_entrada)}
                </span>
              </div>

              <div style={styles.infoRow}>
                <span style={styles.label}>Salida:</span>
                <span style={styles.value}>
                  {formatearHora(horario.hora_salida)}
                </span>
              </div>

              {horario.minutos_adicionales > 0 && (
                <div style={styles.infoRow}>
                  <span style={styles.label}>Extra:</span>
                  <span style={styles.value}>
                    {horario.minutos_adicionales} min
                  </span>
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <div style={{ ...styles.label, marginBottom: 8 }}>
                  Días laborales:
                </div>
                <div style={styles.diasGrid}>
                  {horario.dias_semana?.map((dia, idx) => (
                    <span key={idx} style={styles.diaChip}>
                      {dia.charAt(0).toUpperCase() + dia.slice(1)}
                    </span>
                  ))}
                </div>
              </div>

              <div style={styles.fechasContainer}>
                <div style={{ 
                  fontSize: 13, 
                  color: isDarkMode ? '#737373' : '#737373', 
                  marginBottom: 6,
                  fontWeight: 500
                }}>
                  Período de vigencia:
                </div>
                <div style={{ 
                  fontSize: 14, 
                  color: isDarkMode ? '#ffffff' : '#0a0a0a', 
                  fontWeight: 600 
                }}>
                  {formatearFecha(horario.fecha_inicio)} 
                  {' → '}
                  {formatearFecha(horario.fecha_fin)}
                </div>
              </div>

              {horario.motivo && (
                <div style={styles.motivoBox}>
                  {horario.motivo}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <style>{`
        @keyframes pulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
          }
          50% { 
            opacity: 0.5; 
            transform: scale(0.8);
          }
        }
      `}</style>
    </div>
  );
}